import { z } from 'zod';
import { DEAL_LOSS_REASONS, DEAL_STATUSES, type DealStatus } from '../enums.js';
import {
  listFiltersQueryFragment,
  optionalDate,
  blankableNumber,
  optionalText,
  paginationQuerySchema,
  toPatchSchema,
  uuidSchema,
} from './common.js';
import { currencySchema } from './organization.js';
import { businessManagerFilterSchema, type BusinessManagerRef, type RecordRef } from './records.js';

export const createDealSchema = z.object({
  name: z.string().trim().min(1, 'Required').max(200),
  amount: z.coerce.number().min(0).max(1e15).default(0),
  currency: currencySchema.optional(),
  /** Omitted, the stage's default probability is used. */
  /** Omitted, the stage's default probability is used; it is never null. */
  probability: blankableNumber(z.coerce.number().int().min(0).max(100)),
  expectedCloseDate: optionalDate(),
  description: optionalText(4000),
  nextStep: optionalText(500),
  /** Required when the deal is created directly into a lost stage; enforced server-side. */
  lostReason: z.enum(DEAL_LOSS_REASONS).nullish(),

  pipelineId: uuidSchema.optional(),
  stageId: uuidSchema.optional(),
  accountId: uuidSchema.nullish(),
  primaryContactId: uuidSchema.nullish(),
  /** Source attribution: which campaign this deal is credited to. */
  campaignId: uuidSchema.nullish(),
  /** The markets this record sells into. Carried across when a lead converts. */
  verticals: z.array(z.string().trim().min(1).max(80)).max(20).optional(),
  businessManagerId: uuidSchema.optional(),
  customFields: z.record(z.string(), z.unknown()).optional(),
});
export type CreateDealInput = z.infer<typeof createDealSchema>;

export const updateDealSchema = toPatchSchema(createDealSchema).extend({
  /** Required when moving a deal into a lost stage; enforced server-side, not just by this schema. */
  lostReason: z.enum(DEAL_LOSS_REASONS).nullish(),
});
export type UpdateDealInput = z.infer<typeof updateDealSchema>;

/** Dedicated endpoint: a stage move writes history and can fire workflows. */
export const moveDealStageSchema = z.object({
  stageId: uuidSchema,
  lostReason: z.enum(DEAL_LOSS_REASONS).nullish(),
});
export type MoveDealStageInput = z.infer<typeof moveDealStageSchema>;

export const DEAL_SORT_FIELDS = [
  'createdAt',
  'updatedAt',
  'name',
  'amount',
  'expectedCloseDate',
] as const;

export const listDealsQuerySchema = paginationQuerySchema.extend({
  sortBy: z.enum(DEAL_SORT_FIELDS).default('createdAt'),
  status: z.enum(DEAL_STATUSES).optional(),
  pipelineId: uuidSchema.optional(),
  stageId: uuidSchema.optional(),
  accountId: uuidSchema.optional(),
  businessManagerId: businessManagerFilterSchema.optional(),
  minAmount: z.coerce.number().min(0).optional(),
  closingBefore: z.iso.date().optional(),
  closingAfter: z.iso.date().optional(),
  ...listFiltersQueryFragment,
});
export type ListDealsQuery = z.infer<typeof listDealsQuerySchema>;

export interface DealStageRef {
  id: string;
  name: string;
  position: number;
  probability: number;
  isWon: boolean;
  isLost: boolean;
  color: string | null;
}

export interface DealSummary {
  id: string;
  name: string;
  /** Decimal as a string: JSON numbers cannot hold money exactly. */
  amount: string;
  currency: string;
  probability: number;
  /**
   * `amount x probability / 100`, computed the same way everywhere it is
   * shown — never stored, so it can never go stale after a stage or amount
   * change. For a Won deal `probability` is 100 (the full amount); for a
   * Lost one it is 0 - both by the pipeline's own stage configuration, not a
   * special case here.
   */
  expectedRevenue: string;
  status: DealStatus;
  expectedCloseDate: string | null;
  actualCloseDate: string | null;
  nextStep: string | null;
  pipeline: RecordRef;
  stage: DealStageRef;
  account: RecordRef | null;
  primaryContact: RecordRef | null;
  verticals: string[];
  businessManager: BusinessManagerRef | null;
  createdAt: string;
  updatedAt: string;
}

export interface DealDetail extends DealSummary {
  /**
   * Where the business came from, copied off the lead at conversion.
   *
   * Read-only: it records what happened, and a deal that can be re-attributed
   * afterwards is a deal whose attribution report means nothing.
   */
  leadSource: string | null;
  description: string | null;
  lostReason: string | null;
  customFields: Record<string, unknown>;
  contacts: { id: string; name: string; role: string | null }[];
  stageHistory: {
    id: string;
    fromStageName: string | null;
    toStageName: string;
    durationSeconds: number | null;
    createdAt: string;
  }[];
}

/** One column of the Kanban board, with its own rollups. */
export interface PipelineColumn {
  stage: DealStageRef;
  deals: DealSummary[];
  /** Totals cover the whole column, not just the page of deals returned. */
  totalCount: number;
  totalValue: string;
  weightedValue: string;
}

export interface PipelineBoard {
  pipeline: { id: string; name: string; isDefault: boolean };
  currency: string;
  columns: PipelineColumn[];
  totals: {
    openCount: number;
    openValue: string;
    weightedValue: string;
    wonValue: string;
    lostValue: string;
  };
}

export const boardQuerySchema = z.object({
  pipelineId: uuidSchema.optional(),
  businessManagerId: businessManagerFilterSchema.optional(),
  /** Cards per column; the column totals are unaffected by it. */
  limitPerStage: z.coerce.number().int().min(1).max(100).default(50),
  /** Same field, same semantics as the list view's — see `listDealsQuerySchema`. */
  closingBefore: z.iso.date().optional(),
  closingAfter: z.iso.date().optional(),
  /** Narrows the cards (and the column totals beside them) to deals matching this text. */
  search: z.string().trim().min(1).max(200).optional(),
});
export type BoardQuery = z.infer<typeof boardQuerySchema>;

export interface PipelineSummary {
  id: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  isActive: boolean;
  stages: DealStageRef[];
}
